import React, { useContext, useState, useEffect } from 'react';
import {AppContext} from "../../context/app-context";
import request from "../../util/SuperagentUtils";
import Busy from "../../components/Busy";
import {useSnackbar} from "../../context/snackbar-context";
import { getErrorMessageForNonStandardAndStandardResponse } from "../../util/NetworkErrorUtil";
import { SupplierNotificationSettingsVO } from "../../types/model/valueObject/notificationSettings/SupplierNotificationSettingsVO";
import { SupplierNotificationSettingRecipientRoleVO } from "../../types/model/valueObject/notificationSettings/SupplierNotificationSettingRecipientRoleVO";
import { useReactiveGetRequest } from "../../hooks/request/useReactiveGetRequest";
import { notificationEndpoints } from "../../requests/api-endpoints";
import { NotificationSettingsPanel } from "../../components/notifications/NotificationSettingsPanel";
import {
  NotificationRoleDefinitionType,
  NotificationChannelDefinitionType,
  NotificationCategoryDefinitionType,
  NotificationUniversalSettingDefinitionType,
  NotificationUniversalSettingType,
  NotificationTypeChannels,
  applyOwnerFallback,
  buildNotificationSettingsFromData,
  serializeRecipientRoles,
  NotificationChannelsDefinitions
} from "../../util/NotificationUtil";

const SupplierChannels: NotificationChannelDefinitionType[] = [
  NotificationChannelsDefinitions.EMAIL
];

const SupplierCategories: NotificationCategoryDefinitionType[] = [
  {
    value: 'ADMINISTRATIVE',
    label: 'Administrative',
    tooltip: 'Receive notifications for administrative updates, such as changes to your account or billing information',
  },
  {
    value: 'PARTNER_YARD_MANAGEMENT',
    label: 'Yard Management',
    tooltip: 'Receive notifications related to partner yard management, such as vehicle assignments or changes to yard operations',
  },
];

// First role is the guaranteed recipient (Account Owner) — see the fallback rule
// in NotificationUtil. The `sub` lines are descriptive labels shown in the matrix.
const SupplierRoles_MARKETPLACE_ONLY: NotificationRoleDefinitionType[] = [
  {value: 'PRIMARY_OWNER', label: 'Account Owner', sub: 'Primary account holder'},
  {value: 'OWNER', label: 'Administrator', sub: 'Manages users & billing'},
];

const SupplierRoles_GMS_PRO: NotificationRoleDefinitionType[] = [
  ...SupplierRoles_MARKETPLACE_ONLY,
  {value: 'GATEMANAGER', label: 'Gate Manager', sub: 'Runs yard gate operations'},
  {value: 'GATECLERK', label: 'Gate Clerk', sub: 'Handles check-in / check-out'},
];

const SupplierUniversalSettings: readonly NotificationUniversalSettingDefinitionType[] = [
  {
    propertyName: 'useLocationRestrictions',
    label: 'Use Location Restrictions',
    tooltip: 'If checked, only users who have access to the location associated with the ' +
             'notification will receive the notification where applicable. User location associations can be set up in the ' +
             'Users section of your account.',
  },
];

const SupplierNotificationSettings = () => {
  const { snackbarShowMessage } = useSnackbar();
  const {user} = useContext(AppContext);
  const SupplierRoles = (user?.subscriptionType === "GMS_PRO") ? SupplierRoles_GMS_PRO : SupplierRoles_MARKETPLACE_ONLY;

  const [notificationSettings, setNotificationSettings] = useState<NotificationTypeChannels>();
  const [universalSettings, setUniversalSettings] = useState<NotificationUniversalSettingType>();
  const [changesMade, setChangesMade] = useState<boolean>(false);

  const { data } = useReactiveGetRequest<SupplierNotificationSettingsVO>({ requestUrl: notificationEndpoints.supplier.get, shouldAbort: true });

  useEffect(() => {
    // Leaves state undefined until the GET resolves, so the matrix never flashes
    // the all-default (owner-locked) state before the saved data arrives.
    setNotificationSettings(buildNotificationSettingsFromData(data, SupplierChannels, SupplierCategories, SupplierRoles));
  }, [data, SupplierRoles]);

  useEffect(() => {
    // Wait for the GET to resolve before deriving the account-wide toggles, so
    // they don't flash unchecked before the saved values arrive.
    if (!data) return;
    const settings: NotificationUniversalSettingType = {}
    Object.keys(data).filter(key => key !== 'recipientRoles').forEach(key => {
      settings[key] = Boolean(data[key as keyof SupplierNotificationSettingsVO]);
    })
    setUniversalSettings(settings);
  }, [data, SupplierRoles]);

  const onSubmit = () => {
    if (!notificationSettings || !universalSettings) return;
    Busy.set(true);

    // Materialise the Account Owner fallback so the saved payload always keeps
    // at least one recipient per (channel, category).
    const effectiveSettings = applyOwnerFallback(notificationSettings, SupplierRoles);
    const recipientRoles: SupplierNotificationSettingRecipientRoleVO[] = serializeRecipientRoles(effectiveSettings) as unknown as SupplierNotificationSettingRecipientRoleVO[];

    const payload: SupplierNotificationSettingsVO = {
      recipientRoles,
      ...universalSettings
    } as SupplierNotificationSettingsVO;

    request
      .patch(notificationEndpoints.supplier.patch)
      .send(payload)
      .then(() => {
        setChangesMade(false);
        Busy.set(false);
        snackbarShowMessage('Notifications settings saved successfully!', 'success', 8000);
      }).catch(error => {
        Busy.set(false);
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error', 15000);
      });
  }

  const handleNotificationSettingsChange = (channel: string, category: string, role: string, value: boolean) => {
    setNotificationSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [channel]: {
          ...prev[channel],
          [category]: {
            ...prev[channel][category],
            [role]: value,
          },
        },
      };
    });
    setChangesMade(true);
  };

  const handleUniversalSettingsChange = (settingName: string, value: boolean) => {
    setUniversalSettings(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        [settingName]: value
      }
    });
    setChangesMade(true);
  }

  return (
    <NotificationSettingsPanel
      roles={SupplierRoles}
      channels={SupplierChannels}
      categories={SupplierCategories}
      settings={notificationSettings}
      onToggle={handleNotificationSettingsChange}
      changesMade={changesMade}
      onSave={onSubmit}
      globalSettingDefinitions={SupplierUniversalSettings}
      globalValues={universalSettings}
      onGlobalToggle={handleUniversalSettingsChange}
    />
  );
}

export { SupplierNotificationSettings };
